/* eslint-disable i18next/no-literal-string */
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Link,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();

  const handleBack = () => {
    navigateBack("/login");
  }

  return (
    <>
    <Box
      sx={{
        minHeight: '100vh',
        minWidth: '100vw',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1,
      }}
    >
    </Box>
      <Box sx={{ 
        minHeight: '100vh',
        py: 4, 
        borderRadius: 3, 
        zIndex: 3, 
        position: "relative",
        left: "50%",
        transform: "translateX(-50%)",
      }}>
        <Container maxWidth="md">
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
            {/* Header */}
            <Box display="flex" alignItems="top" gap={3}>
              <Box>
                <IconButton onClick={handleBack}>
                  <ArrowBackIcon />
                </IconButton>
              </Box>
              <Box>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  Privatlivspolitik
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Sidst opdateret: {new Date().toLocaleDateString('da-DK')}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Dataansvarlig */}
            <Section title="Dataansvarlig">
              <Typography paragraph>
                Vi er dataansvarlig for behandlingen af de personoplysninger, som vi behandler om vores kunder og samarbejdspartnere. Du finder vores kontaktoplysninger nedenfor.
              </Typography>
              <Typography paragraph>
                <strong>DEN-TEC ApS</strong><br />
                Højdevej 35 B, 2830 Virum<br />
                CVR-nr.: 75148011
              </Typography>
              <Typography paragraph>
                Hvis du har spørgsmål til behandlingen af dine personoplysninger, kan du kontakte os via{' '}
                <Link href="mailto:info@den-tec.dk">info@den-tec.dk</Link>.
              </Typography>
            </Section>

            {/* Behandlingsaktiviteter */}
            <Section title="Behandlingsaktiviteter">
              <Typography paragraph>
                Som dataansvarlig jf. GDPR har vi følgende behandlingsaktiviteter.
              </Typography>
            </Section>

            {/* Besøg på applikation */}
            <SubSection title="Besøg i applikationen">
              <Typography paragraph>
                Når du bruger vores applikation, anvender vi local storage i din browser for at applikationen kan fungere korrekt. Vi gemmer:
              </Typography>
              <Typography component="div" paragraph>
                <ul>
                  <li>
                    Login-sessioner{' '}
                    <Tooltip title="Access tokens og refresh tokens samt tidligere afdelinger og den seneste afdeling du har logget ind i">
                      <InfoOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                    </Tooltip>
                  </li>
                  <li>
                    Brugerindstillinger og præferencer{' '}
                    <Tooltip title="Visningstype (liste/kort), tema, sprog">
                      <InfoOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
                    </Tooltip>
                  </li>
                  <li>Midlertidig data til at forbedre brugeroplevelsen</li>
                </ul>
              </Typography>
              <Typography paragraph>
                Disse data er nødvendige for at applikationen kan fungere. Data slettes når du logger ud eller når din session udløber.
              </Typography>
              <Typography paragraph>
                <strong>Bemærk:</strong> Følgende indstillinger forbliver gemt efter logout for at forbedre din oplevelse ved næste login: sprogvalg, temavalg og seneste afdelinger du har logget ind i.
              </Typography>
            </SubSection>

            {/* Kunder */}
            <SubSection title="Kunder">
              <Typography paragraph>
                Vi har behov for at kommunikere med vores kunder for at sikre, at ydelsen leveres korrekt. Herigennem behandler vi oplysninger om:
              </Typography>
              <Typography component="div" paragraph>
                <ul>
                  <li>Kontaktpersoners navne og kontaktinformation</li>
                  <li>Klinikens navn og adresse</li>
                  <li>Fakturerings- og betalingsinformation</li>
                  <li>Brugsdata og systemlogs</li>
                </ul>
              </Typography>
              <Typography paragraph>
                <strong>Vigtig bemærkning:</strong> Vi behandler IKKE data om jeres patienter (tandlægernes kunder). Disse data forbliver hos jer som dataansvarlig, og vi fungerer kun som teknisk platform.
              </Typography>
              <Typography paragraph>
                Hjemlen til at behandle disse personoplysninger er databeskyttelsesforordningens artikel 6, stk. 1 litra b (kontraktopfyldelse).
              </Typography>
              <Typography paragraph>
                Vi opbevarer kundedata, så længe kontraktforholdet er aktivt, og i op til 5 år efter kontraktens ophør af hensyn til bogføringskrav.
              </Typography>
            </SubSection>

            {/* Medarbejdere */}
            <SubSection title="Medarbejdere hos vores kunder">
              <Typography paragraph>
                For at vores kunder kan bruge systemet, gemmer vi information om deres medarbejdere:
              </Typography>
              <Typography component="div" paragraph>
                <ul>
                  <li>Navn</li>
                  <li>Brugernavn/login-oplysninger</li>
                  <li>Krypterede adgangskoder (saltet + SHA256)</li>
                  <li>Roller og tilladelser i systemet</li>
                  <li>Aktivitetslogs</li>
                  <li>
                    Automatiserede fejllogs (indeholder IP-adresse og UserAgent-information vedrørende
                    brugerens aktive browser og version). Fejllogs slettes automatisk efter 30 dage.
                  </li>
                </ul>
              </Typography>
              <Typography paragraph>
                Hjemlen til at behandle disse personoplysninger er databeskyttelsesforordningens artikel 6, stk. 1 litra b (kontraktopfyldelse med vores kunde).
              </Typography>
            </SubSection>

            {/* Bogføring */}
            <SubSection title="Bogføring">
              <Typography paragraph>
                Vi skal gemme alle regnskabsbilag jf. bogføringsloven. Det betyder, at vi gemmer fakturaer og lignende bilag til brug for regnskabsføring. Heraf kan der fremgå almindelige personoplysninger som navn, adresse og ydelsesbeskrivelse.
              </Typography>
              <Typography paragraph>
                Vores hjemmel til at behandle personoplysninger til bogføringen er databeskyttelsesforordningens artikel 6, stk. 1 litra c (lovkrav).
              </Typography>
              <Typography paragraph>
                Vi opbevarer disse oplysninger i minimum 5 år efter at indeværende regnskabsår er afsluttet.
              </Typography>
            </SubSection>

            {/* Databehandlere */}
            <Section title="Databehandlere">
              <Typography paragraph>
                Vi benytter os af eksterne databehandlere til at levere vores service. Det er vores ansvar at sikre, at dine personoplysninger behandles ordentligt. Derfor stiller vi høje krav til vores samarbejdspartnere, og vores partnere skal garantere, at dine personoplysninger er beskyttet.
              </Typography>
              <Typography paragraph>
                Vi indgår databehandleraftaler med virksomheder, der håndterer personoplysninger på vores vegne for at højne sikkerheden af dine personoplysninger.
              </Typography>
              <Typography paragraph>
                <em>Liste over databehandlere vil blive opdateret løbende.</em>
              </Typography>
            </Section>

            {/* Videregivelse */}
            <Section title="Videregivelse af personoplysninger">
              <Typography paragraph>
                Vi videregiver ikke dine personoplysninger til tredjemand, medmindre det er nødvendigt for at levere vores service (via databehandlere som nævnt ovenfor) eller hvis vi er forpligtet til det ved lov.
              </Typography>
            </Section>

            {/* Tredjelandeoverførsler */}
            <Section title="Tredjelandeoverførsler">
              <Typography paragraph>
                Vi benytter som udgangspunkt databehandlere i EU/EØS, eller som opbevarer data i EU/EØS.
              </Typography>
              <Typography paragraph>
                I nogle tilfælde er dette ikke muligt, og her kan der benyttes databehandlere udenfor EU/EØS, hvis disse kan give dine personoplysninger en passende beskyttelse gennem standardkontraktbestemmelser eller andre godkendte overførselsmekanismer.
              </Typography>
            </Section>

            {/* Behandlingssikkerhed */}
            <Section title="Behandlingssikkerhed">
              <Typography paragraph>
                Vi holder behandlingen af personoplysninger sikker ved at have indført passende tekniske og organisatoriske foranstaltninger:
              </Typography>
              <Typography component="div" paragraph>
                <ul>
                  <li>Krypteret opbevaring af adgangskoder (saltet + SHA256)</li>
                  <li>Sikker datatransmission (HTTPS/TLS)</li>
                  <li>Adgangskontrol og rollebaserede tilladelser</li>
                  <li>Regelmæssige sikkerhedsopdateringer</li>
                  <li>Backup af data</li>
                  <li>Løbende awareness-træning af medarbejdere om GDPR</li>
                </ul>
              </Typography>
            </Section>

            {/* Rettigheder */}
            <Section title="De registreredes rettigheder">
              <Typography paragraph>
                Du har efter databeskyttelsesforordningen en række rettigheder i forhold til vores behandling af oplysninger om dig. Hvis du vil gøre brug af dine rettigheder, skal du kontakte os.
              </Typography>

              <SubSection title="Ret til at se oplysninger (indsigtsret)">
                <Typography paragraph>
                  Du har ret til at få indsigt i de oplysninger, som vi behandler om dig, samt en række yderligere oplysninger.
                </Typography>
              </SubSection>

              <SubSection title="Ret til berigtigelse (rettelse)">
                <Typography paragraph>
                  Du har ret til at få urigtige oplysninger om dig selv rettet.
                </Typography>
              </SubSection>

              <SubSection title="Ret til sletning">
                <Typography paragraph>
                  I særlige tilfælde har du ret til at få slettet oplysninger om dig, inden tidspunktet for vores almindelige generelle sletning indtræffer.
                </Typography>
              </SubSection>

              <SubSection title="Ret til begrænsning af behandling">
                <Typography paragraph>
                  Du har i visse tilfælde ret til at få behandlingen af dine personoplysninger begrænset. Hvis du har ret til at få begrænset behandlingen, må vi fremover kun behandle oplysningerne - bortset fra opbevaring - med dit samtykke, eller med henblik på at retskrav kan fastlægges, gøres gældende eller forsvares, eller for at beskytte en person eller vigtige samfundsinteresser.
                </Typography>
              </SubSection>

              <SubSection title="Ret til indsigelse">
                <Typography paragraph>
                  Du har i visse tilfælde ret til at gøre indsigelse mod vores ellers lovlige behandling af dine personoplysninger.
                </Typography>
              </SubSection>

              <SubSection title="Ret til at transmittere oplysninger (dataportabilitet)">
                <Typography paragraph>
                  Du har i visse tilfælde ret til at modtage dine personoplysninger i et struktureret, almindeligt anvendt og maskinlæsbart format samt at få overført disse personoplysninger fra én dataansvarlig til en anden uden hindring.
                </Typography>
              </SubSection>

              <Typography paragraph sx={{ mt: 2 }}>
                Du kan læse mere om dine rettigheder i Datatilsynets vejledning om de registreredes rettigheder, som du finder på{' '}
                <Link href="https://www.datatilsynet.dk" target="_blank" rel="noopener">
                  www.datatilsynet.dk
                </Link>
              </Typography>
            </Section>

            {/* Klage */}
            <Section title="Klage til Datatilsynet">
              <Typography paragraph>
                Du har ret til at indgive en klage til Datatilsynet, hvis du er utilfreds med den måde, vi behandler dine personoplysninger på. Du finder Datatilsynets kontaktoplysninger på{' '}
                <Link href="https://www.datatilsynet.dk" target="_blank" rel="noopener">
                  www.datatilsynet.dk
                </Link>
              </Typography>
            </Section>
          </Paper>
        </Container>
      </Box>
    </>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function SubSection({ title, children }) {
  return (
    <Box sx={{ mb: 2, ml: 2 }}>
      <Typography variant="h6" fontWeight="600" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}